import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormArray, ReactiveFormsModule, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import { LoggerService } from "@bequeathed/ngx-app-core";
import { FormControlPipe } from "@bequeathed/ngx-utils";
import { BaseSelectionControl } from '../base-selection-control/base-selection-control.component';
import { KeyValuePipe, NgClass } from "@angular/common";
import { CheckboxValidator } from "../../../validators/checkbox.validator";
import { InterviewState } from "@bequeathed/ngx-services";

const LOG: LoggerService = LoggerService.get('CheckboxControl');

/**
 * Control to display checkboxes
 *
 * @author Dan Bennett (dbennett)
 */
@Component({
    selector: 'checkbox-control',
    templateUrl: './checkbox-control.component.html',
    styleUrls: [
        './checkbox-control.component.sass',
        '../../base-form/base-form.component.sass',
        '../base-input-control/base-input-control.component.sass'
    ],
    standalone: true,
    imports: [
        // Angular
        KeyValuePipe,
        NgClass,
        ReactiveFormsModule,
        // Bequeathed
        FormControlPipe,
    ],
    encapsulation: ViewEncapsulation.None
})
export class CheckboxControl extends BaseSelectionControl implements OnInit {

    private normalizedControlName: string | undefined;

    constructor() {
        super();

        this.inputType = 'checkbox';
        this.inputClass = '';
    }

    override ngOnInit(): void {
        if (this.question.required) {
            this.validators.push(CheckboxValidator.requiredCheckboxGroupValidator);
        }
        // Pre-select from the stored answer directly rather than gating on `editingResponse`. The flag
        // is only set to true by BaseControl.ngOnInit (via userResponse), which runs AFTER this method
        // builds the FormArray, and on some render paths (e.g. the chat-interface topic-jump edit) it is
        // never bound as an @Input at all. Reading userResponse/calculatedResponse here makes the
        // checkbox pre-tick correctly on every edit path. The value is a '|'-delimited string.
        const storedResponse: string | null | undefined = this.question.userResponse ?? this.question.calculatedResponse;
        const previousValue: string[] = storedResponse ? storedResponse.split('|') : [];
        const qControl: UntypedFormArray = new UntypedFormArray([], { validators: this.validators });
        this.question.acceptedValues.forEach((key, value) => {
            const control: UntypedFormControl = new UntypedFormControl(previousValue.indexOf(value) > -1);
            // @ts-expect-error-linting-its-good
            control['id'] = value;
            qControl.push(control);
        });
        this.control = qControl;

        super.ngOnInit();
    }

    override applyValue(state: InterviewState) {
        const values: string[] = [];
        (this.control as FormArray).controls.filter((ctrl: any) => !!ctrl.value).forEach((ctrl: any) => values.push(ctrl.id));
        if (this.standalone) {
            state.currentQuestion!.userResponse = values.join("|");
        }
    }

    isChecked(): boolean {
        return this.control.value;
    }

    isHoriz(): boolean {
        return this.inputClass === 'options-wrapper-horizontal';
    }

    get formArray() {
        return this.control as UntypedFormArray;
    }

    getInputId(i: number): string {
        return 'collapsibleCheckboxDescription-' + this.normalizedControlName + '-' + i;
    }

    override trim() {
        LOG.trace('trim', 'Not compatible with trim()');
    }
}
